import React, { FC, useRef, useEffect, useState } from "react";
import { Box, Radio, RadioGroup, FormControlLabel } from "@mui/material";

interface FivePointScaleProps {
  value: string;
  onChange: (value: string) => void;
  labels: string[];
}

interface LinePosition {
  left: number;
  width: number;
}

export const FivePointScale: FC<FivePointScaleProps> = ({
  value,
  onChange,
  labels,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const labelRefs = useRef<(HTMLLabelElement | null)[]>([]);
  const [lines, setLines] = useState<LinePosition[]>([]);

  useEffect(() => {
    const updateLines = (): void => {
      if (!containerRef.current) return;

      const containerRect = containerRef.current.getBoundingClientRect();
      const newLines: LinePosition[] = [];
      const offset = 14;

      for (let i = 0; i < labelRefs.current.length - 1; i++) {
        const current = labelRefs.current[i];
        const next = labelRefs.current[i + 1];

        if (current && next) {
          const currentRect = current.getBoundingClientRect();
          const nextRect = next.getBoundingClientRect();

          const currentCenter = currentRect.left + currentRect.width / 2;
          const nextCenter = nextRect.left + nextRect.width / 2;

          newLines.push({
            left: currentCenter - containerRect.left + offset,
            width: nextCenter - currentCenter - offset * 2,
          });
        }
      }

      setLines(newLines);
    };

    updateLines();
    window.addEventListener("resize", updateLines);

    return () => window.removeEventListener("resize", updateLines);
  }, []);

  const normalizedLabels = Array.from({ length: 5 }, (_, index) => {
    const candidate = labels?.[index]?.trim();

    if (candidate && candidate.length > 0) {
      return candidate;
    }

    const ordinal = index + 1;

    return ordinal.toString();
  });

  // Check if any label is likely multiline (length > 15 chars or contains newline)
  const hasMultilineLabels = normalizedLabels.some(
    (label) => label.length > 15 || label.includes("\n")
  );

  return (
    <Box ref={containerRef} sx={{ position: "relative", mt: 2 }}>
      {lines.map((line, index) => (
        <Box
          key={index}
          sx={{
            position: "absolute",
            top: hasMultilineLabels ? "16px" : "17px",
            left: `${line.left}px`,
            width: `${line.width}px`,
            height: "3px",
            bgcolor: "rgba(0, 0, 0, 0.2)",
            zIndex: 0,
          }}
        />
      ))}
      <RadioGroup
        row
        value={value}
        onChange={(e) => onChange(e.target.value)}
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          position: "relative",
          zIndex: 1,
        }}
      >
        {[1, 2, 3, 4, 5].map((radioValue, index) => (
          <FormControlLabel
            key={radioValue}
            ref={(el) => {
              labelRefs.current[index] = el as HTMLLabelElement | null;
            }}
            value={radioValue.toString()}
            control={<Radio />}
            label={normalizedLabels[index]}
            labelPlacement="bottom"
            sx={{
              mx: 0,
              flex: "0 1 80px",
              alignItems: "center",
              "& .MuiFormControlLabel-label": {
                display: "inline-block",
                width: "100%",
                maxWidth: 80,
                whiteSpace: "normal",
                wordBreak: "break-word",
                textAlign: "center",
                lineHeight: 1.1,
              },
            }}
          />
        ))}
      </RadioGroup>
    </Box>
  );
};
